import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { displays } from 'investira.react.lib';
import { formats, validators } from 'investira.sdk';
import { Chip, Stack, Typography, useMediaQuery } from 'investira.react.components';
import { PessoaImage } from '../../molecules';
import { SETORES } from '../../../enums';
import utils from '../../../utils';

/**
 * Renderiza uma empresa retornada pela busca contextual.
 *
 * @param {Object} pProps Propriedades do componente.
 * @param {Object} pProps.pessoa Empresa apresentada como sugestão.
 * @param {boolean} pProps.showSetor Usa o setor específico como classificação principal.
 * @param {Function} pProps.onClick Callback acionado ao selecionar a empresa.
 * @returns {JSX.Element} Item clicável com identificação e setor da empresa.
 */
const SearchEmpresasItem = memo(pProps => {
  const xTopSetor = validators.isEmpty(pProps.pessoa.top_setor)
    ? SETORES[pProps.pessoa.top_setor_id]?.setor
    : pProps.pessoa.top_setor;
  const xPrimaryChipLabel = pProps.showSetor ? pProps.pessoa.setor : xTopSetor;
  const xPessoaTipoList = utils.helpers.getPessoaTipoById(pProps.pessoa.pessoa_tipo_id);
  const isUpMd = useMediaQuery(pTheme => pTheme.breakpoints.up('md'));

  return (
    <Stack
      id={pProps.pessoa.pessoa_id}
      direction="row"
      spacing={2}
      alignItems="flex-start"
      component="li"
      onClick={pProps.onClick}
      sx={{
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        borderBottom: '1px solid',
        borderBottomColor: 'background.main',
        '&:hover': { backgroundColor: 'background.main' }
      }}>
      <PessoaImage
        logoPessoaId={pProps.pessoa.logo_pessoa_id}
        alt={pProps.pessoa.pessoa}
        hasLogo={pProps.pessoa.logo_ok}
        size={36}
      />
      <Stack flexGrow={1} minWidth={0} spacing={0.5}>
        <Stack direction="row" minWidth={0}>
          <Stack flexGrow={1} minWidth={0}>
            <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600 }}>
              {pProps.pessoa.pessoa_fantasia || pProps.pessoa.pessoa}
            </Typography>
          </Stack>
          {isUpMd && xPrimaryChipLabel && (
            <Stack alignItems="flex-end" ml={1} minWidth={0} maxWidth="40%">
              <Chip
                label={xPrimaryChipLabel}
                size="small"
                color="primary"
                sx={{
                  maxWidth: '100%',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              />
            </Stack>
          )}
        </Stack>
        {pProps.pessoa.cnpj && (
          <Typography variant="caption" color="textSecondary">
            {displays.cnpj(pProps.pessoa.cnpj)}
          </Typography>
        )}
        <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: '8px 8px' }}>
          {!isUpMd && xPrimaryChipLabel && (
            <Chip
              label={xPrimaryChipLabel}
              size="small"
              color="primary"
              sx={{
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }
              }}
            />
          )}
          {!pProps.showSetor && !validators.isEmpty(pProps.pessoa.setor) && (
            <Chip label={pProps.pessoa.setor} size="small" color="secondary" />
          )}
          {xPessoaTipoList.map(pPessoaTipo => (
            <Chip
              key={pPessoaTipo.id}
              label={pPessoaTipo.descricao}
              size="small"
              color="secondary"
            />
          ))}
          {pProps.pessoa.b3?.codigo_emissor && (
            <Chip label={pProps.pessoa.b3.codigo_emissor} size="small" color="secondary" />
          )}
          {!validators.isNull(pProps.pessoa.pl) && (
            <Chip
              label={`Patrimônio líquido: ${formats.friendlyNumber(pProps.pessoa.pl, 2, true)}`}
              size="small"
              color="secondary"
            />
          )}
          {!validators.isNull(pProps.pessoa.cs) && (
            <Chip
              label={`Capital social: ${formats.friendlyNumber(pProps.pessoa.cs, 2, true)}`}
              size="small"
              color="secondary"
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
});

SearchEmpresasItem.propTypes = {
  pessoa: PropTypes.object.isRequired,
  showSetor: PropTypes.bool,
  onClick: PropTypes.func.isRequired
};

SearchEmpresasItem.defaultProps = {
  showSetor: false
};

SearchEmpresasItem.displayName = 'SearchEmpresasItem';

export default SearchEmpresasItem;
